import type { GameMode, PvpHeroData } from '@roe/shared';
import { GameStateManager } from '../../state/GameStateManager.js';
import { MultiplayerLobbyBase } from './MultiplayerLobbyBase.js';

/** Minimum stage required to join the PVP queue. Mirrors server constant
 *  (`packages/server/src/multiplayer/matchmaking.ts` PVP_MIN_STAGE). */
export const PVP_MIN_STAGE = 5;

const PVP_THEME_COLOR = 0x8b2000;
const PVP_BACKGROUND_KEY = 'zone_background_rift';

/**
 * Concrete PVP variant. The save slot is selected on the main menu and
 * passed in via `init({ saveSlot })`, so the lobby never shows a picker —
 * the base class renders a single left-side save card. The Join Queue
 * button builds a `pvpHeroes` payload from the selected save.
 */
export class PvpLobbyScene extends MultiplayerLobbyBase {
  protected readonly mode: GameMode = 'pvp';

  private gsm = GameStateManager.getInstance();

  constructor() {
    super('PvpLobbyScene');
  }

  protected getThemeColor(): number {
    return PVP_THEME_COLOR;
  }

  protected getModeLabel(): string {
    return '⚔  PvP Lobby';
  }

  protected getBackgroundKey(): string {
    return PVP_BACKGROUND_KEY;
  }

  protected getQueueBlockedReason(): string {
    if (this.selectedSaveSlot === undefined) return 'Sélectionne une sauvegarde avant de rejoindre la queue.';
    const info = this.gsm.getSaveSlotInfo(this.selectedSaveSlot);
    if (!info) return "Cette sauvegarde n'existe pas. Retour au menu pour en choisir une autre.";
    if (info.level < PVP_MIN_STAGE) {
      return `Niveau ${PVP_MIN_STAGE} requis pour le PvP (actuellement: ${info.level}).`;
    }
    return 'Cette sauvegarde ne peut pas rejoindre la queue PvP.';
  }

  protected getQueuePayload(): { mode: GameMode; stageLevel: number; saveSlot: number; pvpHeroes: PvpHeroData[] } | undefined {
    if (this.selectedSaveSlot === undefined) return undefined;
    const info = this.gsm.getSaveSlotInfo(this.selectedSaveSlot);
    if (!info) return undefined;
    if (info.level < PVP_MIN_STAGE) return undefined;
    const pvpHeroes: PvpHeroData[] = info.heroData.map((h) => ({
      heroClass: h.heroClass,
      heroKey: h.heroKey,
      name: h.name,
      level: h.level,
    }));
    return {
      mode: 'pvp',
      stageLevel: info.level,
      saveSlot: this.selectedSaveSlot,
      pvpHeroes,
    };
  }
}
